/**
 * Domain-Shaped Query Key Factory
 * 
 * Provides structured, hierarchical query keys for all application domains.
 * Following TanStack Query best practices:
 * - Broad keys allow broad invalidation (e.g. `queryKeys.appointments.all`)
 * - Specific keys allow targeted invalidation (e.g. `queryKeys.appointments.detail(id)`)
 * - Filter/param-scoped keys isolate cache entries (e.g. `queryKeys.appointments.list(filters)`)
 */

export const queryKeys = {
  // --------------------------------------------------------------------------
  // Appointments Domain
  // --------------------------------------------------------------------------
  appointments: {
    all: ['appointments'],
    lists: () => [...queryKeys.appointments.all, 'list'],
    list: (filters = {}) => [...queryKeys.appointments.lists(), filters],
    details: () => [...queryKeys.appointments.all, 'detail'],
    detail: (id) => [...queryKeys.appointments.details(), String(id)],
    fetchAll: (interval) => [...queryKeys.appointments.all, 'fetchAll', interval],
    messages: (appointmentId) => [...queryKeys.appointments.detail(appointmentId), 'messages'],
    availability: (doctorId, date, consultationType) => {
      const base = [...queryKeys.appointments.all, 'availability', String(doctorId), String(date)];
      return consultationType ? [...base, String(consultationType)] : base;
    },
    reasons: (type) => [...queryKeys.appointments.all, 'reasons', type],
  },


  // --------------------------------------------------------------------------
  // Configurations Domain
  // --------------------------------------------------------------------------
  configs: {
    all: ['configs'],
    getQuersions: () => [...queryKeys.configs.all, 'querstions'],
    getAppointmentBookingStep1: () => [...queryKeys.configs.all, 'getAppointmentBookingStep1'],
  },

  notifications: {
    all: ['notifications'],
    notificationsUnreadCount: () => [...queryKeys.notifications.all, 'unread-count'],
    notificationsList: () => [...queryKeys.notifications.all, 'notificationsList'],
  },

  // --------------------------------------------------------------------------
  // Doctor/Patient Messages Domain (sidebar "Messages" unread badge)
  // --------------------------------------------------------------------------
  messages: {
    all: ['messages'],
    unreadCount: (userId) => [...queryKeys.messages.all, 'unread-count', String(userId || 'current')],
  },

  // --------------------------------------------------------------------------
  // Collaboration Domain
  // --------------------------------------------------------------------------
  collaborations: {
    all: ['collaborations'],
    lists: () => [...queryKeys.collaborations.all, 'list'],
    list: (filters = {}) => [...queryKeys.collaborations.lists(), filters],
    details: () => [...queryKeys.collaborations.all, 'detail'],
    detail: (id) => [...queryKeys.collaborations.details(), String(id)],
    notes: (id) => [...queryKeys.collaborations.detail(id), 'notes'],
    consultationNote: (id, appointmentId) => [...queryKeys.collaborations.detail(id), 'appointment', String(appointmentId), 'note'],
    activeForPatient: (patientId) => [
      ...queryKeys.collaborations.all,
      'patient',
      String(patientId),
      'active',
    ],
    doctors: (filters = {}) => [...queryKeys.collaborations.all, 'doctors', filters],
    patients: (query = '') => [...queryKeys.collaborations.all, 'patients', String(query)],
    specialties: () => [...queryKeys.collaborations.all, 'specialties'],
  },

  // --------------------------------------------------------------------------
  // Patients Domain
  // --------------------------------------------------------------------------
  patients: {
    all: ['patients'],
    lists: () => [...queryKeys.patients.all, 'list'],
    list: (filters = {}) => [...queryKeys.patients.lists(), filters],
    details: () => [...queryKeys.patients.all, 'detail'],
    detail: (id) => [...queryKeys.patients.details(), String(id)],
    members: (patientId) => [...queryKeys.patients.detail(patientId), 'members'],
    timeline: (patientId) => [...queryKeys.patients.detail(patientId), 'timeline'],
    categories: () => [...queryKeys.patients.all, 'categories'],
    medicalBackground: (patientId, doctorId, appointmentId) => {
      const base = [...queryKeys.patients.all, 'medical-background', String(patientId)];
      if (doctorId) base.push(String(doctorId));
      if (appointmentId) base.push(String(appointmentId));
      return base;
    },
    allergies: () => [...queryKeys.patients.all, 'allergies'],
    allergensByGroup: (group) => [...queryKeys.patients.allergies(), 'group', group],
    antecedents: () => [...queryKeys.patients.all, 'antecedents'],
    antecedentTitles: () => [...queryKeys.patients.antecedents(), 'titles'],
    antecedentOptions: (title) => [...queryKeys.patients.antecedents(), 'options', title],
    diagnostics: () => [...queryKeys.patients.all, 'diagnostics'],
    indicators: (patientId) => [...queryKeys.patients.detail(patientId), 'indicators'],
    indicatorCategories: () => [...queryKeys.patients.all, 'indicator-categories'],
  },

  // --------------------------------------------------------------------------
  // Observations Domain
  // --------------------------------------------------------------------------
  observations: {
    all: ['observations'],
    lists: () => [...queryKeys.observations.all, 'list'],
    list: (filters = {}) => [...queryKeys.observations.lists(), filters],
    byPatient: (patientId) => [...queryKeys.observations.all, 'patient', String(patientId)],
    byAppointment: (appointmentId, type) => {
      const base = [...queryKeys.observations.all, 'appointment', String(appointmentId)];
      return type ? [...base, String(type)] : base;
    },
    timeline: (patientId) => [...queryKeys.observations.all, 'timeline', String(patientId)],
    etapes: (patientId) => [...queryKeys.observations.all, 'etapes', String(patientId)],
    cards: (doctorId) => [...queryKeys.observations.all, 'cards', String(doctorId || 'current')],
    templates: (doctorId) => [...queryKeys.observations.all, 'templates', String(doctorId || 'current')],
    template: (doctorId, templateId) => [...queryKeys.observations.templates(doctorId), String(templateId)],
    examinations: (doctorId) => [...queryKeys.observations.all, 'examinations', String(doctorId || 'current')],
    services: (doctorId) => [...queryKeys.observations.all, 'services', String(doctorId || 'current')],
  },

  // --------------------------------------------------------------------------
  // Charts Domain
  // --------------------------------------------------------------------------
  charts: {
    all: ['charts'],
    catalog: () => [...queryKeys.charts.all, 'catalog'],
    properties: (doctorId, patientId) => [
      ...queryKeys.charts.all,
      'properties',
      String(doctorId),
      String(patientId),
    ],
    property: (propertyId, doctorId, patientId) => [
      ...queryKeys.charts.all,
      'property',
      String(propertyId),
      String(doctorId),
      String(patientId),
    ],
    patientWeights: (doctorId, patientId) => [
      ...queryKeys.charts.all,
      'weights',
      String(doctorId),
      String(patientId),
    ],
    appointmentWeights: (doctorId, patientId, appointmentId) => [
      ...queryKeys.charts.all,
      'weights',
      String(doctorId),
      String(patientId),
      String(appointmentId),
    ],
    patientPoints: (doctorId, patientId) => [
      ...queryKeys.charts.all,
      'points',
      String(doctorId),
      String(patientId),
    ],
  },

  // --------------------------------------------------------------------------
  // Prescriptions Domain
  // --------------------------------------------------------------------------
  prescriptions: {
    all: ['prescriptions'],
    lists: () => [...queryKeys.prescriptions.all, 'list'],
    byAppointment: (appointmentId) => [...queryKeys.prescriptions.all, 'appointment', String(appointmentId)],
    byPatient: (patientId) => [...queryKeys.prescriptions.all, 'patient', String(patientId)],
    templates: (doctorId) => [...queryKeys.prescriptions.all, 'templates', String(doctorId)],
    prescriptionTemplates: {
      all: () => [...queryKeys.prescriptions.all, 'prescriptionTemplates'],
      search: (name = '') => [
        ...queryKeys.prescriptions.all,
        'prescriptionTemplates',
        'search',
        String(name || '').trim().toLowerCase(),
      ],
      detail: (id) => [
        ...queryKeys.prescriptions.all,
        'prescriptionTemplates',
        'detail',
        String(id),
      ],
    },
    specialTemplates: () => [...queryKeys.prescriptions.all, 'specialTemplates'],
    configMedication: (params) => [
      ...queryKeys.prescriptions.all,
      'configMedication',
      ...(params ? [JSON.stringify(params)] : []),
    ],
    categoriesWithTests: (params) => [
      ...queryKeys.prescriptions.all,
      'categoriesWithTests',
      ...(params && Object.keys(params).length > 0 ? [params] : []),
    ],
    treatments: (doctorId) => [...queryKeys.prescriptions.all, 'treatments', String(doctorId)],
    detail: (id) => [...queryKeys.prescriptions.all, 'detail', String(id)],
  },

  // --------------------------------------------------------------------------
  // Waiting Room Domain
  // --------------------------------------------------------------------------
  waitingRoom: {
    all: ['waitingRoom'],
    byDoctor: (doctorId) => [...queryKeys.waitingRoom.all, 'doctor', String(doctorId)],
    patients: (roomId) => [...queryKeys.waitingRoom.all, 'room', String(roomId), 'patients'],
    details: (roomId) => [...queryKeys.waitingRoom.all, 'room', String(roomId), 'details'],
  },

  // --------------------------------------------------------------------------
  // User & Auth Domain
  // --------------------------------------------------------------------------
  user: {
    all: ['user'],
    current: () => [...queryKeys.user.all, 'current'],
    account: () => [...queryKeys.user.all, 'account'],
    profile: () => [...queryKeys.user.all, 'profile'],
  },

  // --------------------------------------------------------------------------
  // Assistant Chat Domain
  // --------------------------------------------------------------------------
  assistantChat: {
    all: ['assistantChat'],
    chatroom: (role, userId) => [...queryKeys.assistantChat.all, 'chatroom', String(role || 'Doctor'), String(userId || 'default')],
    messages: (chatroomId) => [...queryKeys.assistantChat.all, 'messages', String(chatroomId)],
    unreadCount: (chatroomId) => [...queryKeys.assistantChat.all, 'unreadCount', String(chatroomId)],
  },


  // --------------------------------------------------------------------------
  // Localisation data (cities, cities municipalities)
  // --------------------------------------------------------------------------
  locations: {
    all: ['locations'],
    cities: () => [...queryKeys.locations.all, 'cities'],
    citiesMunicipalities: () => [...queryKeys.locations.all, 'cities-municipalities'],
  },

  // --------------------------------------------------------------------------
  // Scans Domain
  // --------------------------------------------------------------------------
  scans: {
    all: ['scans'],
    lastScans: (doctorId) => [...queryKeys.scans.all, 'last', String(doctorId)],
  },

  // --------------------------------------------------------------------------
  // Medications Domain
  // --------------------------------------------------------------------------
  medications: {
    all: ['medications'],
    list: () => [...queryKeys.medications.all, 'list'],
  },

  // --------------------------------------------------------------------------
  // Odontogram Domain (clinical: never persisted, see useOdontogramQuery)
  // --------------------------------------------------------------------------
  odontogram: {
    all: ['odontogram'],
    byAppointment: (appointmentId) => [...queryKeys.odontogram.all, 'appointment', String(appointmentId)],
  },

  // --------------------------------------------------------------------------
  // BMI Domain (clinical: never persisted, see useBmiQuery)
  // --------------------------------------------------------------------------
  bmi: {
    all: ['bmi'],
    appointment: (appointmentId) => [...queryKeys.bmi.all, 'appointment', String(appointmentId)],
    byAppointment: (appointmentId, limit) => [...queryKeys.bmi.appointment(appointmentId), { limit: limit ?? null }],
  },

  // --------------------------------------------------------------------------
  // Consultation plugin settings kept per doctor (shortcuts)
  // --------------------------------------------------------------------------
  pluginSettings: {
    all: ['pluginSettings'],
    shortcuts: () => [...queryKeys.pluginSettings.all, 'shortcuts'],
  },

  // --------------------------------------------------------------------------
  // Vaccination calendar plugin (clinical: never persisted, see useVaccinationQuery)
  // --------------------------------------------------------------------------
  vaccination: {
    all: ['vaccination'],
    byAppointment: (appointmentId) => [...queryKeys.vaccination.all, 'appointment', String(appointmentId)],
  },

  // --------------------------------------------------------------------------
  // Consultation file explorer (clinical: never persisted, see useDoctorFilesQuery)
  // --------------------------------------------------------------------------
  doctorFiles: {
    all: ['doctorFiles'],
    byPatient: (patientId) => [...queryKeys.doctorFiles.all, 'patient', String(patientId)],
    comments: (patientId) => [...queryKeys.doctorFiles.all, 'comments', String(patientId)],
    storage: () => [...queryKeys.doctorFiles.all, 'storage'],
    usage: () => [...queryKeys.doctorFiles.all, 'usage'],
  },

};

export default queryKeys;
